import React, { useState, useEffect, useCallback } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight,
    faTimes,
    faChevronLeft,
    faChevronRight as faChevronRightSolid,
    faSearchPlus,
    faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
    getPortfolioCategories,
    getPortfolioItems,
    getPortfolioImageUrl,
} from '../services/portfolioService';

export default function Portfolio() {
    const [categories, setCategories]         = useState([]);
    const [activeCategory, setActiveCategory] = useState(null); // { id, name }
    const [items, setItems]                   = useState([]);
    const [catsLoading, setCatsLoading]       = useState(true);
    const [itemsLoading, setItemsLoading]     = useState(false);
    const [catsError, setCatsError]           = useState(null);
    const [itemsError, setItemsError]         = useState(null);
    const [lightboxOpen, setLightboxOpen]     = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // ── Fetch categories on mount ────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            setCatsLoading(true);
            setCatsError(null);
            try {
                const cats = await getPortfolioCategories();
                setCategories(cats);
                if (cats.length > 0) setActiveCategory(cats[0]);
            } catch (err) {
                setCatsError(err.message || 'Failed to load categories.');
            } finally {
                setCatsLoading(false);
            }
        };
        load();
    }, []);

    // ── Fetch items whenever active category changes ─────────────────────────
    useEffect(() => {
        if (!activeCategory) return;
        const load = async () => {
            setItemsLoading(true);
            setItemsError(null);
            setItems([]);
            try {
                const data = await getPortfolioItems(activeCategory.id);
                setItems(data);
            } catch (err) {
                setItemsError(err.message || 'Failed to load portfolio items.');
            } finally {
                setItemsLoading(false);
            }
        };
        load();
    }, [activeCategory]);

    // ── Lightbox helpers ─────────────────────────────────────────────────────
    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = useCallback(() => {
        setLightboxOpen(false);
        document.body.style.overflow = 'auto';
    }, []);

    const goToNext = useCallback(() => {
        setCurrentImageIndex(prev =>
            prev === items.length - 1 ? 0 : prev + 1
        );
    }, [items.length]);

    const goToPrevious = useCallback(() => {
        setCurrentImageIndex(prev =>
            prev === 0 ? items.length - 1 : prev - 1
        );
    }, [items.length]);

    useEffect(() => {
        const handleKey = (e) => {
            if (!lightboxOpen) return;
            if (e.key === 'Escape')      closeLightbox();
            if (e.key === 'ArrowRight')  goToNext();
            if (e.key === 'ArrowLeft')   goToPrevious();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [lightboxOpen, closeLightbox, goToNext, goToPrevious]);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Header />

            <section className="breadcrumbsection">
                <div className='container'>
                    <h1>Portfolio</h1>
                    <ul className='breadcrumblinks'>
                        <li><Link to="/">Home</Link></li>
                        <li><FontAwesomeIcon icon={faChevronRight} /></li>
                        <li>Portfolio</li>
                    </ul>
                </div>
            </section>

            <section className="portfolio-main-section gallery-section">
                <div className="container">

                    {/* ── Category tabs ── */}
                    {catsLoading && (
                        <div className="blogs-loading">
                            <FontAwesomeIcon icon={faSpinner} spin className="blogs-spinner" />
                            <p>Loading categories...</p>
                        </div>
                    )}

                    {!catsLoading && catsError && (
                        <div className="blogs-error"><p>{catsError}</p></div>
                    )}

                    {!catsLoading && !catsError && categories.length > 0 && (
                        <div className="portfolio-tabs-wrapper">
                            <div className="portfolio-tabs">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        className={`portfolio-tab ${activeCategory?.id === cat.id ? 'active' : ''}`}
                                        onClick={() => setActiveCategory(cat)}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Items grid ── */}
                    {itemsLoading && (
                        <div className="blogs-loading">
                            <FontAwesomeIcon icon={faSpinner} spin className="blogs-spinner" />
                            <p>Loading portfolio...</p>
                        </div>
                    )}

                    {!itemsLoading && itemsError && (
                        <div className="blogs-error"><p>{itemsError}</p></div>
                    )}

                    {!itemsLoading && !itemsError && items.length === 0 && activeCategory && (
                        <div className="blogs-empty">
                            <p>No items found in this category yet.</p>
                        </div>
                    )}

                    {!itemsLoading && !itemsError && items.length > 0 && (
                        <div className="portfolio-grid">
                            {items.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="portfolio-grid-item"
                                    onClick={() => openLightbox(index)}
                                >
                                    <div className="portfolio-image-wrapper">
                                        <img
                                            src={getPortfolioImageUrl(item.image)}
                                            alt={`${activeCategory?.name} ${index + 1}`}
                                            className="portfolio-image"
                                        />
                                        <div className="portfolio-overlay">
                                            <div className="portfolio-zoom-icon">
                                                <FontAwesomeIcon icon={faSearchPlus} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            </section>

            {/* ── Lightbox ── */}
            {lightboxOpen && items[currentImageIndex] && (
                <div className="portfolio-lightbox" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>

                    <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); goToPrevious(); }}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>

                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getPortfolioImageUrl(items[currentImageIndex].image)}
                            alt={`${activeCategory?.name} ${currentImageIndex + 1}`}
                            className="lightbox-image"
                        />
                        <div className="lightbox-counter">
                            {currentImageIndex + 1} / {items.length}
                        </div>
                    </div>

                    <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); goToNext(); }}>
                        <FontAwesomeIcon icon={faChevronRightSolid} />
                    </button>
                </div>
            )}

            <Newsletter />
            <Footer />
        </>
    );
}